import { useState, useEffect } from 'react';
import { useApiClient } from '../hooks/useApiClient';
import './CreateEnvironment.css';

interface AvailableOptions {
  environments: string[];
  homeApplications: Array<{
    id: string;
    name: string;
    bucket: string;
  }>;
}

interface AutoPopulatedConfig {
  environmentName: string;
  identityServerUrl: string;
  targetEnvironment: string;
  modulithMicroserviceUrl: string;
  cdnUrl: string;
  frontendDistributionId: string | null;
  apiGatewayId: string | null;
  ecsCluster: string;
  ecsService: string | null;
  dynamoDbTable: string;
  frontendBucket: string;
  _metadata: {
    apiGatewayMatched: boolean;
    cloudfrontMatched: boolean;
    ecsServiceMatched: boolean;
    apiGatewayName: string | null;
    cloudfrontDomain: string | null;
    warning?: string;
  };
}

function CreateEnvironment() {
  // Step 1: User provides minimal input
  const [ephemeralCode, setEphemeralCode] = useState('');
  const [targetEnvironment, setTargetEnvironment] = useState('');
  const [homeApplication, setHomeApplication] = useState('');

  // Step 2: Available options (loaded from backend)
  const [availableOptions, setAvailableOptions] = useState<AvailableOptions | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Step 3: Auto-populated configuration
  const [autoPopulated, setAutoPopulated] = useState<AutoPopulatedConfig | null>(null);
  const [isAutoPopulating, setIsAutoPopulating] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Step 4: Submission
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const apiClient = useApiClient();

  // Load available options on mount
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const options = await apiClient.get('/available-options');
        setAvailableOptions(options);
      } catch (err: any) {
        console.error('Failed to load available options:', err);
        setError('Failed to load environment options. Please refresh the page.');
      } finally {
        setLoadingOptions(false);
      }
    };

    loadOptions();
  }, []);

  // Auto-populate when user provides all 3 inputs
  useEffect(() => {
    if (ephemeralCode && targetEnvironment && homeApplication) {
      handleAutoPopulate();
    } else {
      setAutoPopulated(null);
    }
  }, [ephemeralCode, targetEnvironment, homeApplication]);

  const handleAutoPopulate = async () => {
    setIsAutoPopulating(true);
    setError(null);

    try {
      const config = await apiClient.post('/auto-populate', {
        ephemeralCode,
        targetEnvironment,
        homeApplication,
      });

      setAutoPopulated(config);

      // Show warning if any resources couldn't be auto-discovered
      if (config._metadata.warning) {
        setError(`Auto-discovery warning: ${config._metadata.warning}`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to auto-populate configuration');
      setAutoPopulated(null);
    } finally {
      setIsAutoPopulating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!autoPopulated) {
      setError('Please provide all required information for auto-population');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Map to the format expected by start-execution Lambda
      const payload = {
        ephemeralEnvCode: autoPopulated.environmentName,
        ephemeralIdsrvUrl: autoPopulated.identityServerUrl,
        modulithMicroserviceUrl: autoPopulated.modulithMicroserviceUrl,
        cdnUrl: autoPopulated.cdnUrl,
        environment: autoPopulated.targetEnvironment,
        tenantIdentifier: autoPopulated.environmentName,
        awsRegion: 'us-east-1', // TODO: Make configurable if needed
        apiGatewayId: autoPopulated.apiGatewayId,
        ecsClusterName: autoPopulated.ecsCluster,
        ecsServiceName: autoPopulated.ecsService,
        dynamodbTableName: autoPopulated.dynamoDbTable,
        homeS3Bucket: autoPopulated.frontendBucket,
        cloudfrontDistributionId: autoPopulated.frontendDistributionId,
        awsAccountId: 'auto', // Will be populated by Lambda
      };

      const response = await apiClient.post('/executions', payload);

      setSuccess(`Environment "${ephemeralCode}" created successfully! Execution ID: ${response.executionId}`);

      // Reset form
      setEphemeralCode('');
      setTargetEnvironment('');
      setHomeApplication('');
      setAutoPopulated(null);
    } catch (err: any) {
      setError(err.message || 'Failed to create environment');
    } finally {
      setLoading(false);
    }
  };

  if (loadingOptions) {
    return (
      <div className="create-environment">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading configuration options...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="create-environment">
      <h2>Create New Ephemeral Environment</h2>
      <p className="subtitle">Provide minimal information - we'll auto-discover the rest</p>

      <form onSubmit={handleSubmit} className="create-form">
        {/* Step 1: Minimal User Input */}
        <div className="form-section">
          <h3>Basic Information</h3>

          <div className="form-group">
            <label htmlFor="ephemeralCode">Ephemeral Code *</label>
            <input
              type="text"
              id="ephemeralCode"
              value={ephemeralCode}
              onChange={(e) => setEphemeralCode(e.target.value)}
              placeholder="e.g., xtzakwvq"
              required
              pattern="[a-z0-9]+"
              title="Only lowercase letters and numbers are allowed"
            />
            <small>Your unique 8-character ephemeral environment identifier</small>
          </div>

          <div className="form-group">
            <label htmlFor="targetEnvironment">Target Environment *</label>
            <select
              id="targetEnvironment"
              value={targetEnvironment}
              onChange={(e) => setTargetEnvironment(e.target.value)}
              required
            >
              <option value="">Select environment...</option>
              {availableOptions?.environments.map((env) => (
                <option key={env} value={env}>
                  {env.toUpperCase()}
                </option>
              ))}
            </select>
            <small>The base environment to clone (dev, qa, staging)</small>
          </div>

          <div className="form-group">
            <label htmlFor="homeApplication">Home Application *</label>
            <select
              id="homeApplication"
              value={homeApplication}
              onChange={(e) => setHomeApplication(e.target.value)}
              required
            >
              <option value="">Select home application...</option>
              {availableOptions?.homeApplications.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
            </select>
            <small>The frontend application to deploy</small>
          </div>
        </div>

        {/* Step 2: Auto-Discovery Status */}
        {isAutoPopulating && (
          <div className="discovery-status">
            <div className="spinner-small"></div>
            <p>Auto-discovering AWS resources...</p>
          </div>
        )}

        {/* Step 3: Show Discovered Resources */}
        {autoPopulated && !isAutoPopulating && (
          <div className="form-section discovered-section">
            <h3>Auto-Discovered Configuration</h3>

            <div className="discovery-grid">
              <div className="discovery-item">
                <span className="discovery-label">Identity Server URL:</span>
                <span className="discovery-value">{autoPopulated.identityServerUrl}</span>
                <span className={`discovery-status ${autoPopulated.identityServerUrl ? 'success' : 'error'}`}>
                  {autoPopulated.identityServerUrl ? '✓' : '✗'}
                </span>
              </div>

              <div className="discovery-item">
                <span className="discovery-label">API Gateway:</span>
                <span className="discovery-value">
                  {autoPopulated._metadata.apiGatewayName || 'Not found'}
                </span>
                <span className={`discovery-status ${autoPopulated._metadata.apiGatewayMatched ? 'success' : 'error'}`}>
                  {autoPopulated._metadata.apiGatewayMatched ? '✓' : '✗'}
                </span>
              </div>

              <div className="discovery-item">
                <span className="discovery-label">CloudFront Distribution:</span>
                <span className="discovery-value">
                  {autoPopulated._metadata.cloudfrontDomain || 'Not found'}
                </span>
                <span className={`discovery-status ${autoPopulated._metadata.cloudfrontMatched ? 'success' : 'error'}`}>
                  {autoPopulated._metadata.cloudfrontMatched ? '✓' : '✗'}
                </span>
              </div>

              <div className="discovery-item">
                <span className="discovery-label">ECS Service:</span>
                <span className="discovery-value">
                  {autoPopulated.ecsService || 'Not found'}
                </span>
                <span className={`discovery-status ${autoPopulated._metadata.ecsServiceMatched ? 'success' : 'error'}`}>
                  {autoPopulated._metadata.ecsServiceMatched ? '✓' : '✗'}
                </span>
              </div>

              <div className="discovery-item">
                <span className="discovery-label">ECS Cluster:</span>
                <span className="discovery-value">{autoPopulated.ecsCluster}</span>
                <span className="discovery-status success">✓</span>
              </div>

              <div className="discovery-item">
                <span className="discovery-label">DynamoDB Table:</span>
                <span className="discovery-value">{autoPopulated.dynamoDbTable}</span>
                <span className="discovery-status success">✓</span>
              </div>

              <div className="discovery-item">
                <span className="discovery-label">CDN URL:</span>
                <span className="discovery-value">{autoPopulated.cdnUrl}</span>
                <span className="discovery-status success">✓</span>
              </div>

              <div className="discovery-item">
                <span className="discovery-label">Modulith URL:</span>
                <span className="discovery-value">{autoPopulated.modulithMicroserviceUrl}</span>
                <span className="discovery-status success">✓</span>
              </div>
            </div>

            {/* Advanced: Show all fields for manual override */}
            <button
              type="button"
              className="toggle-advanced"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? 'Hide' : 'Show'} Advanced Options
            </button>

            {showAdvanced && (
              <div className="advanced-options">
                <p className="advanced-warning">
                  ⚠️ Advanced users only. Modifying these values may cause deployment failures.
                </p>
                <pre className="config-json">
                  {JSON.stringify(autoPopulated, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Error/Success Messages */}
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading || !autoPopulated || isAutoPopulating}
          className="submit-button"
        >
          {loading ? 'Creating Environment...' : 'Create Environment'}
        </button>

        {!autoPopulated && ephemeralCode && targetEnvironment && homeApplication && (
          <p className="form-hint">
            Auto-discovery in progress...
          </p>
        )}
      </form>
    </div>
  );
}

export default CreateEnvironment;
